/**
 * File upload step - renders differently based on the selected upload type. For SDS: uploads and
 * parses immediately on file selection. For Document: collects file + vendor via a validated form,
 * then submits.
 */

import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import FileUpload from "@cloudscape-design/components/file-upload"
import FormField from "@cloudscape-design/components/form-field"
import Select from "@cloudscape-design/components/select"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMemo, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { z } from "zod"

import type { Vendor } from "../../services/vendorRegistryApi"
import { UPLOAD_TYPE_OPTIONS, UPLOAD_TYPES, type UploadType } from "./types"

const MAX_SIZE_MB = 50
const EDC_MAX_SIZE_MB = 250

/**
 * Validates a selected file against size and format constraints. Returns an error message, or null
 * when the file is acceptable.
 */
function validateFile(file: File, acceptedFormats: string[], maxSizeMb: number): string | null {
  if (file.size > maxSizeMb * 1024 * 1024) {
    return `File size exceeds maximum of ${maxSizeMb}MB`
  }
  if (acceptedFormats.length > 0) {
    const fileExt = file.name.substring(file.name.lastIndexOf(".")).toLowerCase()
    const normalized = acceptedFormats.map((f) => f.toLowerCase())
    if (!normalized.includes(fileExt)) {
      return `File format ${fileExt} not allowed. Expected: ${acceptedFormats.join(", ")}`
    }
  }
  return null
}

const FILE_UPLOAD_I18N = {
  uploadButtonText: () => "Choose file",
  dropzoneText: () => "Drag and drop your file here, or click to browse",
  removeFileAriaLabel: (index: number) => `Remove file ${index + 1}`,
  errorIconAriaLabel: "Error",
}

interface FileUploadStepProps {
  uploadType: UploadType
  vendors: Vendor[]
  vendorLoadError: string | null
  onBack: () => void
  onSDSFileSelect: (file: File) => void
  parsing: boolean
  parseError: string | null
  onDocSubmit: (file: File, vendorId: string, vendorName: string) => void
  docUploading: boolean
}

export default function FileUploadStep({
  uploadType,
  vendors,
  vendorLoadError,
  onBack,
  onSDSFileSelect,
  parsing,
  parseError,
  onDocSubmit,
  docUploading,
}: FileUploadStepProps) {
  const config = UPLOAD_TYPE_OPTIONS.find((o) => o.type === uploadType)!

  if (uploadType === UPLOAD_TYPES.EDC || uploadType === UPLOAD_TYPES.SDTM) {
    return (
      <SDSFileUpload
        acceptedFormats={config.acceptedFormats}
        maxSizeMb={uploadType === UPLOAD_TYPES.EDC ? EDC_MAX_SIZE_MB : MAX_SIZE_MB}
        onFileSelect={onSDSFileSelect}
        parsing={parsing}
        parseError={parseError}
        onBack={onBack}
        specLabel={
          uploadType === UPLOAD_TYPES.SDTM ? "SDTM mapping specification" : "EDC specification"
        }
      />
    )
  }

  return (
    <DocumentFileUpload
      acceptedFormats={config.acceptedFormats}
      vendors={vendors}
      vendorLoadError={vendorLoadError}
      onSubmit={onDocSubmit}
      uploading={docUploading}
      onBack={onBack}
    />
  )
}

function SDSFileUpload({
  acceptedFormats,
  maxSizeMb,
  onFileSelect,
  parsing,
  parseError,
  onBack,
  specLabel,
}: {
  acceptedFormats: string[]
  maxSizeMb: number
  onFileSelect: (file: File) => void
  parsing: boolean
  parseError: string | null
  onBack: () => void
  specLabel: string
}) {
  const [rejectedFile, setRejectedFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)

  const handleChange = (files: File[]) => {
    const file = files[0]
    if (!file) {
      setRejectedFile(null)
      setFileError(null)
      return
    }
    const error = validateFile(file, acceptedFormats, maxSizeMb)
    if (error) {
      setRejectedFile(file)
      setFileError(error)
      return
    }
    setRejectedFile(null)
    setFileError(null)
    onFileSelect(file)
  }

  return (
    <SpaceBetween size="l">
      {parsing ? (
        <Box textAlign="center" padding={{ vertical: "xl" }}>
          <SpaceBetween size="s" alignItems="center">
            <Spinner size="large" />
            <Box variant="p">Analyzing {specLabel}...</Box>
            <Box variant="small" color="text-body-secondary">
              This may take up to 30 seconds for large files.
            </Box>
          </SpaceBetween>
        </Box>
      ) : (
        <>
          {parseError && <Alert type="error">{parseError}</Alert>}
          <FileUpload
            value={rejectedFile ? [rejectedFile] : []}
            onChange={({ detail }) => handleChange(detail.value)}
            accept={acceptedFormats.join(",")}
            fileErrors={fileError ? [fileError] : undefined}
            constraintText={`Supports: ${acceptedFormats.join(", ")}. Max size: ${maxSizeMb}MB.`}
            i18nStrings={FILE_UPLOAD_I18N}
          />
        </>
      )}
      <Box float="right">
        <Button variant="link" onClick={onBack}>
          Back
        </Button>
      </Box>
    </SpaceBetween>
  )
}

const documentUploadSchema = z.object({
  vendor_id: z.string().min(1, "Vendor is required"),
})

type DocumentUploadFormData = z.infer<typeof documentUploadSchema>

function DocumentFileUpload({
  acceptedFormats,
  vendors,
  vendorLoadError,
  onSubmit,
  uploading,
  onBack,
}: {
  acceptedFormats: string[]
  vendors: Vendor[]
  vendorLoadError: string | null
  onSubmit: (file: File, vendorId: string, vendorName: string) => void
  uploading: boolean
  onBack: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)

  const vendorOptions = useMemo(
    () => vendors.map((v) => ({ value: v.vendor_id, label: v.vendor_name })),
    [vendors],
  )

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<DocumentUploadFormData>({
    resolver: zodResolver(documentUploadSchema),
    defaultValues: { vendor_id: "" },
  })

  const onFormSubmit = (data: DocumentUploadFormData) => {
    if (!file) return
    const vendor = vendors.find((v) => v.vendor_id === data.vendor_id)
    onSubmit(file, data.vendor_id, vendor?.vendor_name ?? "")
  }

  const handleFileChange = (files: File[]) => {
    const next = files[0] ?? null
    if (!next) {
      setFile(null)
      setFileError(null)
      return
    }
    const error = validateFile(next, acceptedFormats, MAX_SIZE_MB)
    if (error) {
      setFileError(error)
      return
    }
    setFileError(null)
    setFile(next)
  }

  return (
    <form onSubmit={(e) => void handleSubmit(onFormSubmit)(e)}>
      <SpaceBetween size="l">
        <FileUpload
          value={file ? [file] : []}
          onChange={({ detail }) => handleFileChange(detail.value)}
          accept={acceptedFormats.join(",")}
          fileErrors={fileError ? [fileError] : undefined}
          showFileSize
          constraintText={`Supports: ${acceptedFormats.join(", ")}. Max size: ${MAX_SIZE_MB}MB.`}
          i18nStrings={FILE_UPLOAD_I18N}
        />

        <FormField
          label={
            <>
              Vendor{" "}
              <Box variant="span" color="text-status-error">
                *
              </Box>
            </>
          }
          errorText={vendorLoadError || errors.vendor_id?.message}
        >
          {!vendorLoadError && (
            <Controller
              control={control}
              name="vendor_id"
              render={({ field }) => {
                const selectedVendor = vendors.find((v) => v.vendor_id === field.value)
                return (
                  <Select
                    selectedOption={
                      selectedVendor
                        ? { value: selectedVendor.vendor_id, label: selectedVendor.vendor_name }
                        : null
                    }
                    onChange={({ detail }) => field.onChange(detail.selectedOption.value ?? "")}
                    placeholder="Select a vendor..."
                    invalid={!!errors.vendor_id}
                    options={vendorOptions}
                  />
                )
              }}
            />
          )}
        </FormField>

        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" formAction="none" onClick={onBack}>
              Back
            </Button>
            <Button variant="primary" loading={uploading} disabled={!file}>
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </SpaceBetween>
        </Box>
      </SpaceBetween>
    </form>
  )
}
